/**
 * Облет захваченных городов в конце раунда (см. scenes/CityCaptureAnimation):
 * камера по очереди подъезжает к каждому городу, на нем огненный фронт
 * «прожигает» цвет прежнего владельца в цвет нового, и только потом
 * показывается модалка захвата. Длительности — в миллисекундах
 */
export const CITY_CAPTURE_ANIMATION = {
  // Переезд камеры к городу и обратно к кадру игрока: мягкий разгон и
  // торможение (Sine in-out)
  cameraMoveMs: 900,
  restoreCameraMs: 900,
  // Пауза перед каждым переездом камеры: облет начинается в конце раунда,
  // когда поле и интерфейс как раз перерисовываются (новый раунд, подмена
  // владельцев), и между городами поле тоже перерисовывается целиком — на
  // телефоне это сотни миллисекунд. Камера, тронувшаяся в такой момент, шла
  // рывками; после паузы основной поток уже свободен
  settleMs: 150,
  // «Среднее отдаление» над городом: ближе стартового кадра партии
  // (0.8–0.9) — сам город крупно, но вокруг видно соседей. Если город не
  // влезает в долю свободной области fitShare, камера отъезжает дальше, но не
  // дальше minZoom. Кадр не уходит за игровую часть поля дальше, чем на
  // fieldPaddingRatio радиуса гекса: у края поля город встает не в центр, а
  // ближе к краю кадра — иначе полкадра занимала бы чернота за подложкой
  preferredZoom: 1.25,
  minZoom: 0.6,
  fitShare: 0.55,
  fieldPaddingRatio: 1.2,

  // Город в цвете прежнего владельца проявляется (заливка, иконка), стоит,
  // прожигается, стоит в новом цвете и гаснет. Все это на 30% быстрее
  // первоначального (было 450/350/2200/700/450): переезды камеры прежние
  introMs: 345,
  beforeBurnPauseMs: 270,
  burnMs: 1690,
  afterBurnHoldMs: 540,
  outroMs: 345,
  // На какой доле прожигания иконка прежнего владельца растворяется, а новая
  // проявляется (на том же месте)
  iconSwapStartShare: 0.45,
  iconSwapEndShare: 0.85,

  // Заливка ячеек города цветом владельца
  fillAlpha: 0.55,

  // Звук прожигания (отложенный, см. configs/deferredSounds) — по очкам города
  // (cityWinPoints захвата): чем крупнее город, тем мощнее звук. Берется
  // первая строка, в которую очки города укладываются включительно. Звук
  // начинается вместе с огненным фронтом
  burnSounds: [
    {maxCityWinPoints: 5, soundId: 'city_capture_burn_small'},
    {maxCityWinPoints: 10, soundId: 'city_capture_burn_medium'},
    {maxCityWinPoints: Infinity, soundId: 'city_capture_burn_large'},
  ],

  // Иконка владельца над центральным кварталом: значок цвета игрока с
  // картинкой фракции/образа. Размеры — в долях радиуса гекса
  icon: {
    badgeRadiusRatio: 0.58,
    imageSizeRatio: 0.92,
    outlineWidthRatio: 0.04,
    outlineColor: 0x1b130b,
    outlineAlpha: 0.9,
  },

  // Огненный фронт: кольца поперек фронта, от внешнего к внутреннему (внутрь —
  // уже прожженная часть, там свечение цвета нового владельца). Ширина — в
  // долях радиуса гекса, offsetRatio — сдвиг кольца внутрь от фронта
  fire: {
    rings: [
      {widthRatio: 0.55, offsetRatio: 0.22, color: 'newOwner', alpha: 0.4},
      {widthRatio: 0.42, offsetRatio: 0, color: 0xff3d0a, alpha: 0.5},
      {widthRatio: 0.2, offsetRatio: 0, color: 0xff8a1f, alpha: 0.95},
      {widthRatio: 0.08, offsetRatio: 0, color: 0xffeeb8, alpha: 1},
    ],
    // Рваный край фронта: радиус качается по углу и во времени — граница
    // прогорает неровно, как на эскизе, а не ползет ровным кругом. Амплитуда —
    // в долях радиуса гекса, wobblePerSecond — скорость колыхания (радиан/с)
    raggedAmplitudeRatio: 0.12,
    raggedPoints: 72,
    wobblePerSecond: 7,
    // Маска огня — ячейки города, чуть раздутые, чтобы фронт задевал и
    // границу по их краю
    maskScale: 1.1,
    // Круг прожигания стартует чуть шире ячейки захватчика и заканчивается за
    // самой дальней вершиной города
    startRadiusRatio: 0.35,
    endPaddingRatio: 0.4,
  },

  // Искры по фронту: сколько штук в кадр и какие
  // Обычное наложение, а не сложение цветов: на светлых подложках (пустыня,
  // солончак) сложенные искры выгорали в белое и пропадали
  sparks: {
    perFrame: 6,
    lifespanMinMs: 400,
    lifespanMaxMs: 950,
    speedMin: 30,
    speedMax: 150,
    // Искры всплывают вверх
    gravityY: -90,
    scaleStart: 1.1,
    textureSize: 16,
    colors: [0xfff2c2, 0xffb02e, 0xff6a1a, 0xe8380d, 'newOwner'],
  },

  // Сторож: если анимация не кончилась за ожидаемое время с запасом (вкладка в
  // фоне останавливает твины, потерянный WebGL), она обрывается, и модалка
  // показывается. Запас кратный: на медленном устройстве (реже 5 кадров в
  // секунду) Phaser урезает шаг времени кадра, и облет идет медленнее часов —
  // его нельзя обрывать на середине
  watchdogSlowdownFactor: 3,
  watchdogPaddingMs: 8000,
  // Сколько ждать картинки иконок перед первым городом
  iconLoadTimeoutMs: 1500,
  // Запас окна облета во времени хода (см. getCityCaptureAnimationDurationMs):
  // захват доезжает до клиента по сети, а иконки владельцев грузятся
  timeWindowPaddingMs: 1500,
  // Открытые к началу облета панели (списки шапки, рынок, карточки) гаснут и
  // после облета возвращаются за это время — как футер (Footer.css)
  panelsFadeMs: 250,
};

/**
 * Сколько игроку отдать на облет захваченных городов — окно во времени хода
 * (gameState.cityCaptureTimeWindow, см. редьюсер CAPTURE_CITIES и
 * getCityCaptureStageExtraMs): все города по очереди, возврат камеры и запас
 * на доставку захвата и загрузку иконок. Считается одинаково на сервере и у
 * клиентов — только из числа захватов
 * @param {number} capturesCount
 * @param {Object} [config]
 * @returns {number}
 */
export const getCityCaptureAnimationDurationMs = (capturesCount, config = CITY_CAPTURE_ANIMATION) => {
  if (!(capturesCount > 0)) {
    return 0;
  }
  const stepMs = config.settleMs + config.cameraMoveMs + config.introMs + config.beforeBurnPauseMs + config.burnMs
    + config.afterBurnHoldMs + config.outroMs;
  return capturesCount * stepMs + config.settleMs + config.restoreCameraMs + config.timeWindowPaddingMs;
};
